<template>
  <div class="api-doc-v3">
    <NotFoundPage v-if="isNotFound" />
    <RedocWrapper
      v-else
      :specOrSpecUrl="spec"
      @changeIsRedocLoaded="onChangeIsRedocLoaded"
    />
    <div
      v-if="showHostSelect"
      class="host-select-wrap"
    >
      <span class="host-select-label">Host</span>
      <AntSelect
        class="host-select"
        :value="host"
        :options="hostOptions"
        placeholder="Host"
        :disabled="!hostOptions.length"
        :getPopupContainer="trigger => trigger.parentNode"
        @change="onChangeHost"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import {
  ref,
  reactive,
  inject,
  computed,
  onBeforeMount,
} from 'vue'

import { useRoute } from 'vue-router'

import {
  getDoc,
  getSectionsByDocId,
  getManagementByEntityType,
  getManagementByEntityTypeAndId,
} from '@/api/apiDocV3'

import {
  Select as AntSelect,
} from 'ant-design-vue'

import NotFoundPage from '@/views/NotFound'
import RedocWrapper from '@/components/RedocWrapper'

import {
  getDocTitle,
  transformToNested,
  renderTableDataHtml,
  renderTableDataByPlatformHtml,
  renderStringifiedJsonTableRowArray,
  buildStringifiedJsonTableHtml,
  processContentLink,
  processContentBrand,
  preserveMarkdownLineBreaks,
  shouldDisplay,
  compareExampleSortKeys,
} from '@/components/ApiDoc/lib/libsV3'

export default {
  name: 'ApiDocV2',
  components: {
    RedocWrapper,
    NotFoundPage,
    AntSelect,
  },
  setup() {
    const nodeEnv = inject('nodeEnv')
    const isDev = inject('isDev')

    const route = useRoute()
    const { wallet: walletName, edition } = route.params

    const isNotFound = ref(false)
    const spec = reactive({})
    const docData = ref({})
    const templateData = ref({})
    const sectionsBySectionId = ref({})
    const sectionTreeData = ref([])
    const apiMap = ref({})
    const platformsMap = ref({})

    const DEV_HOST_STORAGE_KEY = 'apiDocV3Host'

    const domainToHost = domain => (domain || '').replace(/^https?:\/\//, '').replace(/\/$/, '')

    const host = ref(localStorage.getItem(DEV_HOST_STORAGE_KEY) || '')
    const hostOptions = ref([])

    const showHostSelect = computed(() => (
      isDev.value
      || process.env.NODE_ENV === 'development'
      || nodeEnv.value !== 'production'
    ))

    const loadHostOptions = async () => {
      const { data } = await getManagementByEntityType('template')
      if (!data?.length) return

      const hostMap = new Map()
      data.forEach(({ templateName, domain }) => {
        const formattedHost = domainToHost(domain)
        if (!formattedHost || hostMap.has(formattedHost)) return
        hostMap.set(formattedHost, {
          label: templateName ? `${templateName} - ${formattedHost}` : formattedHost,
          value: formattedHost,
        })
      })

      hostOptions.value = [...hostMap.values()].sort((a, b) => a.label.localeCompare(b.label))

      const hosts = hostOptions.value.map(({ value }) => value)
      if (!hosts.includes(host.value)) {
        host.value = hosts[0] || host.value
        if (host.value) localStorage.setItem(DEV_HOST_STORAGE_KEY, host.value)
      }
    }

    const getDocHost = () => {
      if (showHostSelect.value) return host.value
      return window.location.host
    }

    const resetApiDocState = () => {
      isNotFound.value = false
      docData.value = {}
      templateData.value = {}
      sectionsBySectionId.value = {}
      sectionTreeData.value = []
      apiMap.value = {}
      platformsMap.value = {}
      Object.keys(spec).forEach((key) => { delete spec[key] })
    }

    const findLastIndexBySectionType = (treeData) => {
      const result = treeData.reduce((acc, section, index) => {
        const { sectionType } = section
        if (acc[sectionType] == null) acc[sectionType] = 0
        if (index > acc[sectionType]) acc[sectionType] = index
        return acc
      }, {})

      if (!result.appendices) result.appendices = treeData.length - 1

      return result
    }

    const processContent = content => processContentLink(processContentBrand(content, templateData.value.brandText), sectionsBySectionId.value)

    /** field / operation description：保留編輯器換行，並去掉 TipTap hard break 多餘的 \ */
    const processDescription = content => preserveMarkdownLineBreaks(processContent(content))

    const processRestrictedContent = (restricted) => {
      if (!restricted) return ''
      const { content } = restricted

      if (!content) return ''
      if (!shouldDisplay(restricted, docData.value)) return ''
      return content
    }

    const getStringifiedJsonTableHtml = field => buildStringifiedJsonTableHtml({
      rows: renderStringifiedJsonTableRowArray(field, docData.value),
      minify: true,
      getDescriptionHtml: childField => processDescription(childField.content),
    })

    const recurseFieldsToSchema = (currentFieldData = {}, level = 0) => {
      const requiredFields = []

      const properties = (currentFieldData.fieldsOrder || []).reduce((acc, fieldId) => {
        const currentField = currentFieldData.fields[fieldId]
        if (!shouldDisplay(currentField, docData.value)) return acc

        const {
          fieldName,
          fieldType,
          content,
          maxLength,
          numberType,
          decimalPlace,
          required,
        } = currentField || {}

        if (!fieldName) return acc

        if (required) requiredFields.push(fieldName)

        const property = {
          description: processDescription(content),
        }

        switch (fieldType) {
          case 'string':
            property.type = 'string'
            if (maxLength) property.maxLength = maxLength
            break

          case 'number':
            property.type = 'number'
            if (numberType) property.format = numberType
            if (numberType === 'float' && decimalPlace) {
              const precision = 1000000 // 處理浮點數精度問題 (0.1 ** 4 = 0.00010000000000000002)
              property.multipleOf = Math.round(((0.1 ** decimalPlace) * precision)) / precision
            }
            break

          case 'array of strings':
            property.type = 'array'
            property.items = {
              type: 'string',
            }
            break

          case 'array of numbers':
            property.type = 'array'
            property.items = {
              type: 'number',
            }
            break

          case 'array of objects':
            property.type = 'array'
            property.items = {
              type: 'object',
              ...recurseFieldsToSchema(currentField, level + 1),
            }
            break

          case 'object': {
            property.type = 'object'

            const childSchema = recurseFieldsToSchema(currentField, level + 1) // eslint-disable-line no-case-declarations
            if (childSchema.properties) property.properties = childSchema.properties
            if (childSchema.required?.length) property.required = childSchema.required
            break
          }

          case 'stringified json':
            property.type = 'string'
            property.description += `\n\n${getStringifiedJsonTableHtml(currentField)}`
            break

          case 'boolean':
            property.type = 'boolean'
            break

          case 'json':
            property.type = 'string'
            break

          default:
            property.type = 'string'
        }

        acc[fieldName] = property
        return acc
      }, {})

      if (!Object.keys(properties).length) return { type: 'object' }

      const required = requiredFields.filter(name => Object.prototype.hasOwnProperty.call(properties, name))

      return {
        type: 'object',
        properties,
        ...(required.length ? { required } : {}),
      }
    }

    const recurseFieldsToExamples = (currentFieldData = {}, sectionExamples = {}) => {
      const result = {};

      (currentFieldData.fieldsOrder || []).forEach((fieldId) => {
        const currentField = currentFieldData.fields[fieldId]

        const {
          fieldName,
          fieldType,
          examples,
        } = currentField

        if (!shouldDisplay(currentField, docData.value)) return

        if (['stringified json', 'array of objects', 'object'].includes(fieldType)) { // 這三種沒有 examples，子欄位才有
          const exampleValue = recurseFieldsToExamples(currentField, sectionExamples)
          if (!exampleValue) return

          Object.entries(exampleValue).forEach(([exampleName, { value }]) => {
            if (!Object.keys(value).length) return

            if (!result[exampleName]) result[exampleName] = {}
            if (!result[exampleName].value) result[exampleName].value = {}

            if (fieldType === 'stringified json') {
              result[exampleName].value[fieldName] = JSON.stringify(value)
            } else if (fieldType === 'array of objects') {
              result[exampleName].value[fieldName] = []
              result[exampleName].value[fieldName][0] = value
            } else {
              result[exampleName].value[fieldName] = value
            }
          })
        } else if (examples) {
          console.log(fieldName, currentField, { examples, sectionExamples })
          Object.entries(examples).forEach(([exampleId, exampleValue]) => {
            if (!exampleValue) return
            if (!shouldDisplay(sectionExamples[exampleId], docData.value)) return

            const { exampleName } = sectionExamples[exampleId]

            const formatExampleValue = (val) => {
              let resultVal = val
              switch (fieldType) {
                default:
                case 'string':
                  if (val === '\'\'' || val === '""') {
                    resultVal = ''
                  } else if (val === 'null') {
                    resultVal = null
                  } else {
                    resultVal = val
                  }
                  break

                case 'json':
                  try {
                    resultVal = JSON.parse(val)
                  } catch (error) {
                    resultVal = val
                  }
                  break

                case 'number':
                  resultVal = Number(val)
                  break

                case 'boolean':
                  resultVal = val === 'true'
                  break

                case 'array of strings': {
                  if (/^\[\s*\]$/.test(val)) {
                    resultVal = []
                  } else {
                    const normalizeString = str => str
                    // eslint-disable-next-line no-useless-escape
                      .replace(/[\[\]'"]/g, '') // 移除方括號和引號
                      .replace(/\s*,\s*/g, ',') // 移除逗號前後的空格
                      .trim()

                    resultVal = normalizeString(val).split(',')
                  }
                  break
                }

                case 'array of numbers': {
                  if (/^\[\s*\]$/.test(val)) {
                    resultVal = []
                  } else {
                    resultVal = JSON.parse(val)
                  }
                }
              }

              const docCurrency = docData.value.currency
              if (typeof resultVal === 'string' && docCurrency) resultVal = resultVal.replaceAll('{{currency}}', docCurrency)

              return resultVal
            }

            if (exampleValue.arrayLength) {
              // console.log(fieldName, exampleValue)
              if (!result[exampleName].value[fieldName]) result[exampleName].value[fieldName] = []

              const childExampleValue = recurseFieldsToExamples(currentField, sectionExamples)
              // console.log('*****childExampleValue', childExampleValue)

              Object.values(childExampleValue).forEach(({ value }) => {
                // console.log('*****value', value)
                Array.from({ length: exampleValue.arrayLength }).forEach((_, idx) => {
                  Object.entries(value).forEach(([key, val]) => {
                    // console.log('*****key', key, '*****val', val)
                    if (!result[exampleName].value[fieldName][idx]) result[exampleName].value[fieldName][idx] = {}
                    result[exampleName].value[fieldName][idx][key] = val[idx]
                  })
                })
              })
              return
            }

            if (!result[exampleName]) result[exampleName] = {}
            if (!result[exampleName].value) result[exampleName].value = {}

            if (Array.isArray(exampleValue)) {
              exampleValue.forEach((item, idx) => {
                if (!result[exampleName].value[fieldName]) result[exampleName].value[fieldName] = []
                result[exampleName].value[fieldName][idx] = formatExampleValue(item)
              })
              return
            }

            result[exampleName].value[fieldName] = formatExampleValue(exampleValue)
          })
        }
      })

      if (!Object.keys(result).length) return null

      const sortedResult = Object.fromEntries(
        Object.entries(result)
          .sort(([a], [b]) => compareExampleSortKeys(a, b))
          .map(([name, { value }]) => [name, { value }]),
      )

      return sortedResult
    }

    const getAppendixDescription = (appendixTreeData) => {
      const result = appendixTreeData.treeChildren?.reduce((acc, childSection) => {
        if (!shouldDisplay(childSection, docData.value)) return acc
        const {
          treeTitle,
          content,
          restricted,
          tableData,
        } = childSection

        acc += `<h2>${treeTitle}</h2>\n\n`
        acc += `${processContent(content)}\n\n${processRestrictedContent(restricted)}\n\n`

        if (tableData) {
          tableData.columns = childSection.tableColumns
          tableData.platformsOrder = childSection.tablePlatformsOrder

          acc += `${renderTableDataByPlatformHtml({
            tableData,
            idLabelMap: tableData.columns?.api ? apiMap.value : platformsMap.value,
            brandText: templateData.value.brandText,
            docData: docData.value,
            sectionsBySectionId: sectionsBySectionId.value,
          })}\n\n`
        }

        return acc
      }, '')

      return result
    }

    const getApiDoc = async () => {
      const wallet = walletName.match(/^(\w+)-wallet$/)?.[1] || ''
      if (!wallet || !['single', 'multi'].includes(wallet)) {
        isNotFound.value = true
        return
      }

      console.log('nodeEnv', nodeEnv.value)
      const docId = `${getDocHost()}#${wallet}#${edition}`
      const { data: doc } = await getDoc(docId)
      if (!doc) {
        isNotFound.value = true
        return
      }

      docData.value = doc
      console.log('getDoc', docData.value)

      const { data: template } = await getManagementByEntityTypeAndId('template', doc.templateId)
      templateData.value = template

      const docTitle = getDocTitle(doc, { [doc.templateId]: template })
      document.title = docTitle

      console.log('template', template)
      let favicon = document.querySelector('link[rel="icon"]')
      if (!template.faviconUrl) {
        if (favicon) {
          favicon.remove()
        }
        // 強制清除 favicon 快取 - 使用空白圖示覆蓋
        const clearFavicon = document.createElement('link')
        clearFavicon.rel = 'icon'
        clearFavicon.href = 'data:image/x-icon;base64,'
        document.head.appendChild(clearFavicon)

        setTimeout(() => clearFavicon.remove(), 50) // 短暫延遲後移除，確保瀏覽器處理了空白圖示
      } else if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        favicon.href = template.faviconUrl
        document.head.appendChild(favicon)
      } else {
        favicon.href = template.faviconUrl
      }

      const { data: sections } = await getSectionsByDocId(docId)
      if (!sections?.length) {
        isNotFound.value = true
        return
      }
      const treeData = transformToNested({ data: sections })
      sectionTreeData.value = treeData
      console.log('treeData', treeData)

      treeData.forEach((section) => {
        sectionsBySectionId.value[section.sectionId] = section
        if (section.treeChildren) {
          section.treeChildren.forEach((child) => {
            sectionsBySectionId.value[child.sectionId] = child
          })
        }
      })

      const sectionTypeIndexes = findLastIndexBySectionType(treeData)
      console.log('sectionTypeIndexes', sectionTypeIndexes)

      apiMap.value = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.apis + 1).reduce((acc, { treeChildren = [] }) => {
        if (treeChildren.length) {
          treeChildren.forEach(({ operationId, ...child }) => {
            if (operationId && shouldDisplay(child, docData.value)) acc[operationId] = true
          })
        }
        return acc
      }, {})
      console.log('apiMap', apiMap.value)

      const { data: platforms } = await getManagementByEntityType('platform')
      platformsMap.value = platforms.reduce((acc, { id, platformName }) => {
        const docPlatforms = docData.value.platforms || []
        const docExcludedPlatforms = docData.value.excludedPlatforms || []
        if ((!docPlatforms?.length && !docExcludedPlatforms.includes(id)) || (docPlatforms.length && docPlatforms.includes(id))) acc[id] = platformName
        return acc
      }, {})

      platformsMap.value['{{brand}}'] = template.brandText
      console.log('platformsMap', platformsMap.value)

      Object.assign(spec, {
        openapi: '3.0.0',
        servers: [{ url: 'https://{host}' }],
        info: {
          version: null,
          title: docTitle,
          'x-logo': template.logoUrl && {
            url: template.logoUrl,
            altText: `${template.brandText} logo`,
            href: window.location.pathname,
          },
        },
        components: {
          parameters: {
            'Content-Type': {
              name: 'Content-Type',
              in: 'header',
              required: true,
              style: 'simple',
              schema: {
                type: 'string',
                example: 'application/x-www-form-urlencoded',
              },
            },
          },
        },
      })

      spec.info.description = treeData.slice(0, sectionTypeIndexes.info + 1).reduce((acc, section) => {
        if (!shouldDisplay(section, docData.value)) return acc

        const {
          treeTitle,
          treeChildren,
          content,
          tableData,
        } = section

        acc += `# ${treeTitle}\n\n`
        if (content) acc += `${processContent(content)}\n\n`

        if (tableData) {
          tableData.columns = section.tableColumns
          tableData.platformsOrder = section.tablePlatformsOrder

          acc += `${renderTableDataHtml({
            tableData,
            docData: docData.value,
            brandText: template.brandText,
            sectionsBySectionId: sectionsBySectionId.value,
            idLabelMap: platformsMap.value,
          })}\n\n`
        }

        if (treeChildren) {
          treeChildren.forEach((child) => {
            if (shouldDisplay(child, docData.value)) acc += `## ${child.treeTitle}\n\n${processContent(child.content)}\n\n`
          })
        }
        return acc
      }, '')

      spec.tags = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.appendices + 1).map(({ treeTitle, tag }) => ({
        name: tag,
        'x-displayName': treeTitle,
        ...(tag === 'appendix' && { description: getAppendixDescription(treeData[sectionTypeIndexes.appendices]) }),
      }))

      spec.paths = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.apis + 1).reduce((acc, section) => {
        const { tag, treeChildren } = section

        if (treeChildren) {
          treeChildren.forEach((child) => {
            const {
              path,
              treeTitle,
              content,
              operationId,
              request,
              response,
              examples,
            } = child

            if (!shouldDisplay(child, docData.value)) return

            const requestSchema = recurseFieldsToSchema(request)
            const responseSchema = recurseFieldsToSchema(response)
            const hasRequestFields = !!requestSchema.properties

            const requestExamples = recurseFieldsToExamples(request, examples)
            const responseExamples = recurseFieldsToExamples(response, examples)

            acc[path] = {
              post: {
                summary: treeTitle,
                description: processDescription(content),
                operationId,
                // openapisnippet 不解析 $ref，須 inline 否則讀取 parameter.name 會 undefined
                parameters: [{
                  name: 'Content-Type',
                  in: 'header',
                  required: true,
                  schema: {
                    type: 'string',
                    example: 'application/x-www-form-urlencoded',
                  },
                }],
                tags: [tag],
                requestBody: {
                  // description: 'Request Parameters',
                  content: {
                    'application/x-www-form-urlencoded': {
                      schema: requestSchema,
                      ...(requestExamples && { examples: requestExamples }),
                    },
                  },
                  required: hasRequestFields,
                },
                responses: {
                  200: {
                    // description: '',
                    content: {
                      'application/json;charset=UTF-8': {
                        schema: responseSchema,
                        ...(responseExamples && { examples: responseExamples }),
                      },
                    },
                  },
                },
              },
            }
          })
        }

        return acc
      }, {})

      console.log('spec', spec)
    }

    const onChangeHost = (value) => {
      host.value = value
      localStorage.setItem(DEV_HOST_STORAGE_KEY, value)
      resetApiDocState()
      getApiDoc()
    }

    const onChangeIsRedocLoaded = (value) => { if (value && window.opener) window.opener.postMessage({ isRedocLoaded: true }, '*') }

    onBeforeMount(async () => {
      if (showHostSelect.value) {
        await loadHostOptions()
      }
      await getApiDoc()
    })

    return {
      spec,
      onChangeIsRedocLoaded,
      isNotFound,
      showHostSelect,
      host,
      hostOptions,
      onChangeHost,
    }
  },
}
</script>

<style>
.api-doc-v3 {
  position: relative;
}

.host-select-wrap {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.host-select-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

.host-select {
  width: 280px;
}

@media print{
  .host-select-wrap {
    display: none;
  }
  @page {
    size: A4 landscape;
  }
  .platform-table {
    display: table!important;
    table-layout: fixed;
    font-size: 12px;
    overflow: hidden;
  }
  .platform-table th {
    height: 120px;
  }
  .platform-table tr th:first-child {
    width: 90px;
  }
  .vertical {
    width: 45px;
    transform: rotate(90deg) translateX(45px);
    transform-origin: top right;
  }
}
</style>
